import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, filter, firstValueFrom, mergeMap, Observable, of, switchMap } from 'rxjs';
import { ParametersI } from '@interfaces/parameters.interface';
import { UserHelperService } from '@helpers/user-helper.service';
import { PageI } from '@interfaces/page.interface';
import { GlobalParametersNames } from '@enums/global-parameters-names.enum';
import { ParameterTypeE, ParameterMandatoryTabulationE } from '@enums/parameter-type.enum';

@Injectable({
  providedIn: 'root',
})
export class ParametersHttpService {
  private parametersUrl = environment.OMNISMART_API_PARAMETERS;

  private parametersSlug: string = 'parameters/';

  private defaultStorageKey = 'parameters';

  public mandatoryTabulationParameterSubject = new BehaviorSubject<ParametersI | null>(null);
  public mandatoryTabulationParameter$ = this.mandatoryTabulationParameterSubject.asObservable();

  public allowMultipleTabulationsParameterSubject = new BehaviorSubject<ParametersI | null>(null);
  public allowMultipleTabulationsParameter$ = this.allowMultipleTabulationsParameterSubject.asObservable();

  public mandatoryTabulationParameterMessaging$ = this.mandatoryTabulationParameter$.pipe(
    switchMap((valueParamter: ParametersI | null) => {
      const value = valueParamter?.parameter?.mandatoryTabulationOption;
      return of(
        value &&
          [ParameterMandatoryTabulationE.MESSAGING_AND_TELEPHONY, ParameterMandatoryTabulationE.MESSAGING].includes(
            value
          )
      );
    })
  );

  public mandatoryTabulationParameterTelephony$ = this.mandatoryTabulationParameter$.pipe(
    switchMap((valueParamter: ParametersI | null) => {
      const value = valueParamter?.parameter?.mandatoryTabulationOption;
      return of(
        value &&
          [ParameterMandatoryTabulationE.TELEPHONY, ParameterMandatoryTabulationE.MESSAGING_AND_TELEPHONY].includes(
            value
          )
      );
    })
  );

  constructor(private readonly http: HttpClient, private readonly userHelperService: UserHelperService) {}

  public createParameter(parameter: ParametersI): Observable<ParametersI> {
    return this.http.post<ParametersI>(this.parametersUrl + this.parametersSlug, parameter);
  }

  public partialUpdate(id: string, paramtersData: Partial<ParametersI>): Observable<ParametersI> {
    return this.http.patch<ParametersI>(`${this.parametersUrl}${this.parametersSlug}${id}`, paramtersData);
  }

  public saveParameters(parameters: Array<ParametersI>) {
    localStorage.setItem(this.defaultStorageKey, JSON.stringify(parameters));
  }

  public getParamValue<T>(name: GlobalParametersNames, fieldName: string, defaultResult: T): T {
    const prmsStr = localStorage.getItem(this.defaultStorageKey);
    if (prmsStr) {
      const parameters: Array<any> = JSON.parse(prmsStr) ?? [];
      const idx = parameters.findIndex((p) => p?.name === name);
      if (idx !== -1) {
        return parameters[idx][fieldName];
      }
    }
    return defaultResult;
  }
  public getParametersByCustomerId(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt'
  ): Observable<PageI<ParametersI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<ParametersI>>(this.parametersUrl + this.parametersSlug, { params });
  }

  public getCountByCustomerId(): Observable<number> {
    return this.http.get<number>(this.parametersUrl + this.parametersSlug + 'count');
  }

  public findAllByCustomerId(): Observable<PageI<ParametersI>> {
    return this.http.get<PageI<ParametersI>>(this.parametersUrl + this.parametersSlug);
  }

  public getParameterById(id: string): Observable<ParametersI> {
    return this.http.get<ParametersI>(this.parametersUrl + this.parametersSlug + id);
  }

  public updateParameterById(id: string, parameter: ParametersI): Observable<ParametersI> {
    return this.http.patch<ParametersI>(this.parametersUrl + this.parametersSlug + id, parameter);
  }

  public deleteParameterById(id: string): Observable<ParametersI> {
    return this.http.delete<ParametersI>(this.parametersUrl + this.parametersSlug + id);
  }

  public findLastByType(type: ParameterTypeE): Observable<ParametersI> {
    return this.http.get<ParametersI>(this.parametersUrl + this.parametersSlug + 'last-by-type/' + type);
  }

  public async initOnce() {
    const logged = !!this.userHelperService.getUserAttributes();
    if (!logged) {
      return;
    }
    const tabulationParam = await firstValueFrom(this.findLastByType(ParameterTypeE.MANDATORY_TABULATION));
    this.mandatoryTabulationParameterSubject.next(tabulationParam);

    const allowMultipleParam = await firstValueFrom(this.findLastByType(ParameterTypeE.ALLOW_MULTIPLE_TABULATIONS));
    this.allowMultipleTabulationsParameterSubject.next(allowMultipleParam);
  }
}
